import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  useNotifications,
  useUnreadCount,
  useMarkRead,
  useMarkAllRead,
  type AppNotification,
} from "../lib/notifications";
import "../styles.proactive.css";

/**
 * Benachrichtigungszentrum: Glocke + Badge (ungelesen) + Liste (gelesen/ungelesen).
 * (CompanyBrain Welle 2, BEN-106, W2.2). Klick auf einen Eintrag markiert gelesen
 * und springt zum Handeln (Deep-Link).
 */
function timeAgo(iso: string | null): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "gerade eben";
  if (min < 60) return `vor ${min} Min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} Std`;
  const d = Math.floor(h / 24);
  return `vor ${d} Tg`;
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0"
      />
    </svg>
  );
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const unread = useUnreadCount();
  const list = useNotifications("all");
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();
  const nav = useNavigate();
  const count = unread.data?.count ?? 0;

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const items: AppNotification[] = list.data?.items ?? [];

  function openItem(n: AppNotification) {
    if (!n.read) markRead.mutate(n.id);
    if (n.route) {
      setOpen(false);
      nav(n.route);
    }
  }

  return (
    <div className="nb-wrap" ref={ref}>
      <button
        className="nb-btn"
        aria-label={`Benachrichtigungen${count > 0 ? ` (${count} ungelesen)` : ""}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title="Benachrichtigungen"
      >
        <BellIcon />
        {count > 0 && <span className="nb-badge">{count > 99 ? "99+" : count}</span>}
      </button>
      {open && (
        <div className="nb-panel" role="dialog" aria-label="Benachrichtigungszentrum">
          <div className="nb-head">
            <strong>Benachrichtigungen</strong>
            <div className="nb-head-actions">
              {count > 0 && (
                <button className="btn ghost small" onClick={() => markAll.mutate()}>
                  Alle gelesen
                </button>
              )}
              <Link
                to="/app/benachrichtigungen"
                className="nb-cog"
                title="Erinnerungen & Benachrichtigungen einstellen"
                onClick={() => setOpen(false)}
                aria-label="Einstellungen"
              >
                &#9881;
              </Link>
            </div>
          </div>
          <div className="nb-list">
            {list.isLoading && <div className="nb-empty muted small">Wird geladen &hellip;</div>}
            {!list.isLoading && items.length === 0 && (
              <div className="nb-empty muted small">Keine Benachrichtigungen. Erinnerungen koennen Sie in den Einstellungen aktivieren.</div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                className={`nb-item sev-${n.severity} ${n.read ? "read" : "unread"}`}
                onClick={() => openItem(n)}
              >
                <span className="nb-dot" aria-hidden />
                <span className="nb-item-body">
                  <strong>{n.title}</strong>
                  <span className="nb-item-txt">{n.body}</span>
                  <span className="nb-item-meta muted small">
                    {timeAgo(n.created_at)}
                    {n.route ? " · oeffnen →" : ""}
                  </span>
                </span>
              </button>
            ))}
          </div>
          <div className="nb-foot">
            <Link to="/app/benachrichtigungen" onClick={() => setOpen(false)}>
              Erinnerungen &amp; Benachrichtigungen einstellen &rarr;
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
